import { useState, type FormEvent, useEffect } from "react";
import type { Lead, LeadSource, LeadStage, Priority, Industry } from "@/mocks/leadsData";
import { allSources, allStages, allPriorities, allIndustries, allAgents } from "@/mocks/leadsData";
import { fetchApi } from "@/services/apiService";

interface CustomFieldConfig {
  id: string;
  label: string;
  type: string;
  required: boolean;
  showInList?: boolean;
  active?: boolean;
  options?: string[];
  category?: string;
}

type Props = {
  lead: Lead | null;
  onClose: () => void;
  onSave: (data: Omit<Lead, "id" | "lastActivity">, id?: number) => void;
  isSaving?: boolean;
};

export default function AddLeadModal({ lead, onClose, onSave, isSaving }: Props) {
  const isEdit = !!lead;
  const [name, setName] = useState(lead?.name || "");
  const [company, setCompany] = useState(lead?.company || "");
  const [phone, setPhone] = useState(lead?.phone || "");
  const [email, setEmail] = useState(lead?.email || "");
  const [source, setSource] = useState<string>(lead?.source || "Manual");
  const [stage, setStage] = useState<string>(lead?.stage || "New Inquiry");
  const [priority, setPriority] = useState<Priority>(lead?.priority || "Medium");
  const [assigned, setAssigned] = useState(lead?.assigned || "Rajesh Kumar");
  const [industry, setIndustry] = useState<Industry>(lead?.industry || "SaaS");
  const [value, setValue] = useState(lead?.value || "");
  const [projectId, setProjectId] = useState<string>(lead?.projectId || (lead as any)?.project_id || "");

  // Dynamic Custom Fields State
  const [customFieldsConfig, setCustomFieldsConfig] = useState<CustomFieldConfig[]>([]);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, any>>(
    lead?.customFields || (lead as any)?.custom_fields || {}
  );

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved">("idle");

  const [dbSources, setDbSources] = useState<string[]>([]);
  const [dbStages, setDbStages] = useState<string[]>([]);
  const [dbAgents, setDbAgents] = useState<string[]>([]);
  const [projectsList, setProjectsList] = useState<any[]>([]);
  const [agentLeadCounts, setAgentLeadCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    // Fetch dynamic projects
    fetchApi<any[]>("/projects", undefined, [])
      .then((res) => {
        const items = Array.isArray(res) ? res : (res as any)?.data || [];
        setProjectsList(items);
      })
      .catch(() => {});
    // Fetch dynamic sources
    fetchApi<any[]>("/config/lead-sources/get-all", undefined, [])
      .then((res) => {
        const items = Array.isArray(res) ? res : (res as any)?.data || [];
        if (items.length > 0) {
          const names = items.map((s: any) => s.name).filter(Boolean);
          if (names.length > 0) setDbSources(names);
        }
      })
      .catch(() => {});

    // Fetch dynamic pipeline stages
    fetchApi<any[]>("/config/pipeline-stages/get-all", undefined, [])
      .then((res) => {
        const items = Array.isArray(res) ? res : (res as any)?.data || [];
        if (items.length > 0) {
          const names = items.map((s: any) => s.name).filter(Boolean);
          if (names.length > 0) setDbStages(names);
        }
      })
      .catch(() => {});

    // Fetch dynamic custom fields for leads
    fetchApi<any[]>("/config/custom-fields/get-all", undefined, [])
      .then((res) => {
        const items = Array.isArray(res) ? res : (res as any)?.data || [];
        const leadFields = items
          .filter(
            (f: any) => (!f.category || f.category === "lead") && (f.active !== false && f.active !== "false")
          )
          .map((f: any) => {
            let opts: string[] = [];
            if (Array.isArray(f.options)) {
              opts = f.options;
            } else if (typeof f.options === "string") {
              try {
                const parsed = JSON.parse(f.options);
                opts = Array.isArray(parsed) ? parsed : [];
              } catch {
                opts = [];
              }
            }
            return {
              ...f,
              options: opts,
            };
          });
        setCustomFieldsConfig(leadFields);
      })
      .catch(() => {});

    // Fetch team members/users from company users
    fetchApi<any>("/auth/company-user/all", undefined, null)
      .then((res) => {
        const userList = Array.isArray(res)
          ? res
          : Array.isArray(res?.items)
          ? res.items
          : Array.isArray(res?.data?.items)
          ? res.data.items
          : Array.isArray(res?.data)
          ? res.data
          : [];

        const agentNames: string[] = [];
        userList.forEach((t: any) => {
          if (t.name) agentNames.push(t.name);
          else if (t.fullName) agentNames.push(t.fullName);
          else if (t.username) agentNames.push(t.username);
          if (t.leader_name) agentNames.push(t.leader_name);
          if (Array.isArray(t.member_names)) agentNames.push(...t.member_names);
        });
        const unique = Array.from(new Set(agentNames)).filter(Boolean);
        if (unique.length > 0) setDbAgents(unique);
      })
      .catch(() => {});

    // Fetch lead stats including agent lead counts
    fetchApi<any>("/leads/stats", undefined, null)
      .then((res) => {
        const counts = res?.agentLeadCounts || res?.data?.agentLeadCounts;
        if (counts && typeof counts === "object") {
          setAgentLeadCounts(counts);
        }
      })
      .catch(() => {});
  }, []);

  const sourcesList = dbSources.length > 0 ? dbSources : allSources;
  const stagesList = dbStages.length > 0 ? dbStages : allStages;
  const agentsList = dbAgents.length > 0 ? dbAgents : allAgents;

  // Set default selection if empty
  useEffect(() => {
    if (!lead) {
      if (sourcesList.length > 0 && !sourcesList.includes(source)) setSource(sourcesList[0]);
      if (stagesList.length > 0 && !stagesList.includes(stage)) setStage(stagesList[0]);
      if (agentsList.length > 0 && !agentsList.includes(assigned)) setAssigned(agentsList[0]);
    }
  }, [sourcesList, stagesList, agentsList, lead, source, stage, assigned]);

  // Pre-fill custom fields if lead changes
  useEffect(() => {
    if (lead) {
      setCustomFieldValues(lead.customFields || (lead as any).custom_fields || {});
    }
  }, [lead]);

  // Close on Escape key
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  // Autosave indicator — debounce on form change
  useEffect(() => {
    if (!isEdit) return;
    setSaveStatus("saving");
    const timer = setTimeout(() => {
      setSaveStatus("saved");
    }, 1000);
    return () => clearTimeout(timer);
  }, [name, company, phone, email, source, stage, priority, assigned, industry, value, customFieldValues, isEdit]);

  const handleCustomFieldChange = (label: string, val: any) => {
    setCustomFieldValues((prev) => ({ ...prev, [label]: val }));
    if (errors[`custom_${label}`]) {
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated[`custom_${label}`];
        return updated;
      });
    }
  };

  const handleMultiSelectToggle = (label: string, option: string) => {
    const currentList: string[] = Array.isArray(customFieldValues[label]) ? customFieldValues[label] : [];
    const exists = currentList.includes(option);
    const updated = exists ? currentList.filter((o) => o !== option) : [...currentList, option];
    handleCustomFieldChange(label, updated);
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!name.trim()) e.name = "Name is required";

    // Validate required custom fields
    customFieldsConfig.forEach((f) => {
      if (f.required) {
        const val = customFieldValues[f.label];
        if (val === undefined || val === null || val === "" || (Array.isArray(val) && val.length === 0)) {
          e[`custom_${f.label}`] = `${f.label} is required`;
        }
      }
    });

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    onSave(
      {
        name: name.trim(),
        company: company.trim(),
        phone: phone.trim(),
        email: email.trim(),
        source: source as LeadSource,
        stage: stage as LeadStage,
        priority,
        assigned,
        industry,
        value: value.trim() || "₹0",
        score: lead?.score ?? 50,
        scoreFactors: lead?.scoreFactors ?? [],
        projectId: projectId || null,
        project_id: projectId || null,
        customFields: customFieldValues,
      },
      lead?.id
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl w-full max-w-xl shadow-2xl animate-scale-in max-h-[90vh] flex flex-col overflow-hidden">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
          <div>
            <h2 className="text-base font-bold text-gray-900">{isEdit ? "Edit Lead" : "Add New Lead"}</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {isEdit ? "Update lead profile and custom information" : "Enter lead contact details and extra custom fields"}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {saveStatus !== "idle" && (
              <span className={`text-xs font-medium ${saveStatus === "saving" ? "text-gray-400" : "text-green-600"}`}>
                {saveStatus === "saving" ? "Saving..." : "Saved ✓"}
              </span>
            )}
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>
        </div>

        {/* Modal Form Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {/* Core Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="lead-name" className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                Lead Name <span className="text-red-500">*</span>
              </label>
              <input
                id="lead-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full name"
                className="w-full px-3.5 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all"
              />
              {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name}</p>}
            </div>
            <div>
              <label htmlFor="lead-company" className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                Company Name
              </label>
              <input
                id="lead-company"
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder="Company / Organization"
                className="w-full px-3.5 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="lead-phone" className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                Phone Number
              </label>
              <input
                id="lead-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+91 98765 43210"
                className="w-full px-3.5 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all"
              />
            </div>
            <div>
              <label htmlFor="lead-email" className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                Email Address
              </label>
              <input
                id="lead-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="contact@company.com"
                className="w-full px-3.5 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="lead-source" className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                Lead Source
              </label>
              <select
                id="lead-source"
                value={source}
                onChange={(e) => setSource(e.target.value)}
                className="w-full px-3.5 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all cursor-pointer"
              >
                {sourcesList.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="lead-stage" className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                Pipeline Stage
              </label>
              <select
                id="lead-stage"
                value={stage}
                onChange={(e) => setStage(e.target.value)}
                className="w-full px-3.5 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all cursor-pointer"
              >
                {stagesList.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="lead-priority" className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                Priority
              </label>
              <select
                id="lead-priority"
                value={priority}
                onChange={(e) => setPriority(e.target.value as Priority)}
                className="w-full px-3.5 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all cursor-pointer"
              >
                {allPriorities.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="lead-industry" className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                Industry
              </label>
              <select
                id="lead-industry"
                value={industry}
                onChange={(e) => setIndustry(e.target.value as Industry)}
                className="w-full px-3.5 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all cursor-pointer"
              >
                {allIndustries.map((i) => (
                  <option key={i} value={i}>
                    {i}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="lead-assigned" className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                Assigned Agent
              </label>
              <select
                id="lead-assigned"
                value={assigned}
                onChange={(e) => setAssigned(e.target.value)}
                className="w-full px-3.5 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all cursor-pointer"
              >
                {agentsList.map((a) => {
                  const count = agentLeadCounts[a];
                  const countText = count !== undefined ? ` (${count} ${count === 1 ? "lead" : "leads"})` : "";
                  return (
                    <option key={a} value={a}>
                      {a}{countText}
                    </option>
                  );
                })}
              </select>
            </div>
            <div>
              <label htmlFor="lead-value" className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                Deal Value
              </label>
              <input
                id="lead-value"
                type="text"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="e.g. ₹50,000"
                className="w-full px-3.5 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all"
              />
            </div>
          </div>

          {/* Project Association */}
          {projectsList.length > 0 && (
            <div>
              <label htmlFor="lead-project" className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                Associated Project
              </label>
              <select
                id="lead-project"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                className="w-full px-3.5 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all cursor-pointer"
              >
                <option value="">No Project Linked (General Lead)</option>
                {projectsList.map((p) => (
                  <option key={p.id} value={p.id}>
                    🏢 {p.name} {p.client_name ? `(${p.client_name})` : ""}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* ── Dynamic Custom Fields Section ───────────────────── */}
          {customFieldsConfig.length > 0 && (
            <div className="pt-3 border-t border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-list-check-2 text-indigo-600" />
                <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider">
                  Additional Custom Fields
                </h3>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {customFieldsConfig.map((field) => {
                  const fieldVal = customFieldValues[field.label] ?? "";
                  const errorKey = `custom_${field.label}`;
                  const hasError = !!errors[errorKey];

                  return (
                    <div
                      key={field.id || field.label}
                      className={field.type === "Textarea" || field.type === "Multi-select" ? "sm:col-span-2" : ""}
                    >
                      <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                        {field.label} {field.required && <span className="text-red-500">*</span>}
                      </label>

                      {/* Dropdown */}
                      {field.type === "Dropdown" ? (
                        <select
                          value={fieldVal}
                          onChange={(e) => handleCustomFieldChange(field.label, e.target.value)}
                          className="w-full px-3.5 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all cursor-pointer"
                        >
                          <option value="">Select {field.label}...</option>
                          {(field.options || []).map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </select>
                      ) : field.type === "Multi-select" ? (
                        /* Multi-select */
                        <div className="flex flex-wrap gap-2 p-2.5 rounded-lg border border-gray-200 bg-gray-50/50">
                          {(field.options || []).map((opt) => {
                            const isSelected = Array.isArray(fieldVal) && fieldVal.includes(opt);
                            return (
                              <button
                                key={opt}
                                type="button"
                                onClick={() => handleMultiSelectToggle(field.label, opt)}
                                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all cursor-pointer ${
                                  isSelected
                                    ? "bg-indigo-600 text-white shadow-xs"
                                    : "bg-white border border-gray-200 text-gray-700 hover:border-indigo-300"
                                }`}
                              >
                                {isSelected ? "✓ " : "+ "}
                                {opt}
                              </button>
                            );
                          })}
                        </div>
                      ) : field.type === "Date" || field.type === "Date Picker" ? (
                        /* Date */
                        <input
                          type="date"
                          value={fieldVal}
                          onChange={(e) => handleCustomFieldChange(field.label, e.target.value)}
                          className="w-full px-3.5 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all"
                        />
                      ) : field.type === "Toggle" ? (
                        /* Toggle */
                        <div className="flex items-center gap-3 py-1">
                          <button
                            type="button"
                            onClick={() => handleCustomFieldChange(field.label, !fieldVal)}
                            className={`relative w-10 h-5 rounded-full transition-colors duration-200 cursor-pointer ${
                              fieldVal ? "bg-indigo-600" : "bg-gray-300"
                            }`}
                          >
                            <span
                              className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                                fieldVal ? "translate-x-5" : "translate-x-0"
                              }`}
                            />
                          </button>
                          <span className="text-xs text-gray-600 font-medium">
                            {fieldVal ? "Yes / Enabled" : "No / Disabled"}
                          </span>
                        </div>
                      ) : field.type === "Textarea" ? (
                        /* Textarea */
                        <textarea
                          rows={2}
                          value={fieldVal}
                          onChange={(e) => handleCustomFieldChange(field.label, e.target.value)}
                          placeholder={`Enter ${field.label}...`}
                          className="w-full px-3.5 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all"
                        />
                      ) : field.type === "Number" ? (
                        /* Number */
                        <input
                          type="number"
                          value={fieldVal}
                          onChange={(e) => handleCustomFieldChange(field.label, e.target.value)}
                          placeholder="0"
                          className="w-full px-3.5 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all"
                        />
                      ) : (
                        /* Default Text Input */
                        <input
                          type={field.type === "Phone" ? "tel" : field.type === "Email" ? "email" : "text"}
                          value={fieldVal}
                          onChange={(e) => handleCustomFieldChange(field.label, e.target.value)}
                          placeholder={`Enter ${field.label}`}
                          className="w-full px-3.5 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all"
                        />
                      )}

                      {hasError && <p className="text-xs text-red-600 mt-1">{errors[errorKey]}</p>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Modal Footer */}
          <div className="flex items-center gap-2.5 pt-4 border-t border-gray-100 justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="px-4 py-2 rounded-lg border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-xs font-semibold text-white transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 inline-flex items-center gap-2 shadow-sm"
            >
              {isSaving && <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
              {isEdit ? "Update Lead" : "Create Lead"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}